import apiService from '@/services/api.service'
import { clearAllCookies } from '@/common/plugins/axios'
import { getClientVersionDate } from '@/common/constants/appBuild'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAppStore } from '@/stores/app'
import { usePlatformConnectionStore } from '@/stores/platformConnection'
import { useUserPresenceStore } from '@/stores/userPresence'
import { useMenuNotificationIgnoreStore } from '@/stores/menuNotificationIgnore'
import { useMenuFocusStore } from '@/stores/menuFocus'
import { resetStartupActionSession } from '@/services/startup/startupAction.service'
import { translateErrorMessage, t, getErrorMessageForStatus } from '@/common/utils/translations'
import {
  collectEntityRefsFromFields,
  collectRoleIdsForRestrictions,
  normalizeUserFieldsMap,
} from '@/services/server-events/chainUserFields'

// Типы для TypeScript
interface UserProfile {
  username: string
  email: string
  firstName?: string
  lastName?: string
  roles: string[]
  permissions: string[]
  userFields?: Record<string, unknown>
  token?: string
  isProfileFilled?: boolean | null
}

function buildUserProfile(data: any, fallbackUsername?: string, tokenValue?: string): UserProfile {
  const username = data?.username || fallbackUsername || ''
  const fio = data?.fio != null && String(data.fio).trim() !== '' ? String(data.fio).trim() : null
  const firstName = fio ?? data?.firstName ?? username
  const lastName = fio != null ? '' : (data?.lastName ?? '')

  return {
    username,
    email: data?.email || '',
    firstName,
    lastName,
    roles: Array.isArray(data?.roles) ? data.roles : [],
    permissions: Array.isArray(data?.permissions) ? data.permissions : [],
    userFields:
      data?.userFields != null && typeof data.userFields === 'object'
        ? (data.userFields as Record<string, unknown>)
        : undefined,
    token: tokenValue ?? data?.token ?? undefined,
    isProfileFilled: typeof data?.isProfileFilled === 'boolean' ? data.isProfileFilled : null
  }
}

export const useUserStore = defineStore('user', () => {

  // State
  const user = ref<UserProfile | null>(null)
  // Initialize token from localStorage or cookies
  const getInitialToken = (): string | null => {
    const stored = localStorage.getItem('auth_token')
    if (stored) return stored

    // Check cookies
    const cookieStr = document.cookie
    const authTokenMatch = cookieStr.match(/X-AUTH-TOKEN=([^;]+)/)
    if (authTokenMatch) {
      const token = authTokenMatch[1]
      localStorage.setItem('auth_token', token)
      return token
    }

    return null
  }
  const token = ref<string | null>(getInitialToken())
  const isLoading = ref<boolean>(false)

  // Getters (computed)
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userFullName = computed(() => {
    if (!user.value) return ''
    const parts = [user.value.firstName, user.value.lastName].filter(Boolean)
    if (parts.length) {
      return parts.join(' ')
    }
    return user.value.username
  })
  const profileLoaded = computed(
    () => user.value?.userFields != null && typeof user.value.userFields === 'object',
  )
  const platformProfile = computed(() => {
    const appStore = useAppStore()
    const fromUser = user.value?.userFields
    if (fromUser != null) {
      return { userFields: fromUser }
    }
    const chainData = appStore.sessionChainData
    const fallback =
      chainData?.fields?.fields ??
      (chainData?.fields != null && typeof chainData.fields === 'object' ? chainData.fields : null)
    return { userFields: fallback ?? {} }
  })
  const userFieldsMap = computed(() => normalizeUserFieldsMap(platformProfile.value.userFields))
  const roleIds = computed(() => collectRoleIdsForRestrictions(userFieldsMap.value))
  const lineIds = computed(() =>
    collectEntityRefsFromFields(userFieldsMap.value, ['lines']).map((line) => line.getId().serialize()),
  )

  const applyUserProfileResponse = (userData: any, tokenValue?: string) => {
    user.value = buildUserProfile(userData, userData?.username, tokenValue ?? token.value ?? undefined)
  }

  const fetchUserProfile = async (options?: { force?: boolean }): Promise<boolean> => {
    if (!token.value) {
      return false
    }
    if (!options?.force && profileLoaded.value) {
      return true
    }

    try {
      const userData = await apiService.getUser()
      if (userData) {
        applyUserProfileResponse(userData, token.value || undefined)
      }
      return true
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 400 || status === 401 || status === 403) {
        logout()
        return false
      }
      return false
    }
  }

  /** Session ping without overwriting cached userFields. */
  const verifySession = async (): Promise<boolean> => {
    if (!token.value) {
      return false
    }
    if (profileLoaded.value) {
      try {
        await apiService.getUser()
        return true
      } catch (error: any) {
        const status = error?.response?.status
        if (status === 400 || status === 401 || status === 403) {
          logout()
          return false
        }
        return false
      }
    }
    return fetchUserProfile({ force: true })
  }

  /** Future: WebSocket user-profile-changed → patch cached fields without GET /user. */
  const patchUserFields = (payload: Record<string, unknown>) => {
    if (!user.value) {
      return
    }
    user.value = {
      ...user.value,
      userFields: {
        ...(user.value.userFields ?? {}),
        ...payload,
      },
    }
  }

  // Actions
  const initAuth = async (): Promise<boolean> => {
    // Check localStorage first
    let storedToken = localStorage.getItem('auth_token')

    // If no token in localStorage, check cookies
    if (!storedToken) {
      const cookieStr = document.cookie
      const authTokenMatch = cookieStr.match(/X-AUTH-TOKEN=([^;]+)/)
      if (authTokenMatch) {
        storedToken = authTokenMatch[1]
        // Save to localStorage for future use
        localStorage.setItem('auth_token', storedToken)
      }
    }

    const hasSessionCookie = document.cookie.includes('CSRF-TOKEN=') || document.cookie.includes('JSESSIONID=')

    // Quick return: if we have a token, set it and fetch user data in background
    if (storedToken) {
      token.value = storedToken

      // Use stored username as fallback while we fetch real data
      const storedUsername = localStorage.getItem('username') || storedToken.substring(0, 20) || 'user'
      user.value = {
        username: storedUsername,
        email: '',
        firstName: 'User',
        lastName: '',
        roles: [],
        permissions: [],
        token: storedToken
      }

      // Fetch real user data in background (non-blocking)
      // Only fetch if we have session cookies
      if (hasSessionCookie) {
        void fetchUserProfile({ force: false }).catch(() => {})
      }

      return true
    }

    // If no stored token but session cookies exist, try to verify session
    if (hasSessionCookie) {
      token.value = 'session-auth'

      // Create minimal user object and fetch real data in background
      user.value = {
        username: 'user',
        email: '',
        firstName: 'User',
        lastName: '',
        roles: [],
        permissions: [],
        token: 'session-auth'
      }

      void fetchUserProfile({ force: false }).catch(() => {})

      return true
    }

    // No token and no session cookies - not authenticated
    return false
  }

  const signIn = async (credentials: { username: string; password: string; systemAlias?: string | null }) => {
    isLoading.value = true
    try {
      resetStartupActionSession()
      // Only clear auth token from localStorage, not cookies (server needs session/CSRF for login)
      localStorage.removeItem('auth_token')

      await apiService.userSignIn({
        username: credentials.username,
        password: credentials.password,
        systemAlias: credentials.systemAlias || null,
        clientVersionDate: getClientVersionDate(),
      })

      // Wait for cookies to be set by browser (Spring Security sets them via Set-Cookie headers)
      await new Promise(resolve => setTimeout(resolve, 500))

      // Extract token from cookie if available
      const cookieToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('X-AUTH-TOKEN='))
        ?.split('=')[1]

      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('CSRF-TOKEN='))
        ?.split('=')[1]

      // Verify we have authentication cookies
      if (!csrfToken) {
        throw new Error('Authentication failed - CSRF token not received')
      }

      const authToken = cookieToken || csrfToken || 'authenticated'
      token.value = authToken

      if (cookieToken) {
        localStorage.setItem('auth_token', cookieToken)
      }

      localStorage.setItem('username', credentials.username)

      user.value = {
        username: credentials.username,
        email: '',
        firstName: credentials.username,
        lastName: '',
        roles: [],
        permissions: [],
        token: authToken
      }

      await fetchUserProfile({ force: true })

      return {
        success: true,
        user: user.value
      }

    } catch (error: any) {
      let errorMessage = t('errors.authentication')

      // Clear cookies first if we get 403 during login - might be stale session
      if (error.response?.status === 403) {
        clearAllCookies()
      }

      if (error.response?.data?.message) {
        const translated = translateErrorMessage(error.response.data.message)
        errorMessage = translated || error.response.data.message
      } else if (error.response?.data?.error) {
        const translated = translateErrorMessage(error.response.data.error)
        errorMessage = translated || error.response.data.error
      } else if (error.response?.status === 400) {
        errorMessage = t('errors.invalidCredentials')
      } else if (error.response?.status === 401) {
        errorMessage = t('errors.unauthorized')
      } else if (error.response?.status === 403) {
        const isCsrfError = error.response?.data?.message?.toLowerCase().includes('csrf') ||
          error.response?.data?.error?.toLowerCase().includes('csrf') ||
          error.response?.headers?.['x-csrf-error'] === 'true'

        if (isCsrfError) {
          errorMessage = t('errors.security')
        } else {
          errorMessage = t('errors.forbidden.detailed')
        }
      } else if (error.response?.status === 404) {
        errorMessage = t('errors.serverUnavailable')
      } else if (error.response?.status === 503) {
        const translated = error.response?.data?.message ? translateErrorMessage(error.response.data.message) : null
        errorMessage = translated || error.response?.data?.message || t('errors.authServiceUnavailable')
      } else if (error.response?.status === 500) {
        errorMessage = t('errors.internalServerError')
      } else if (error.response?.status) {
        errorMessage = getErrorMessageForStatus(error.response.status)
      } else if (error.message) {
        const translated = translateErrorMessage(error.message)
        errorMessage = translated || error.message
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = t('errors.networkError')
      }

      logout()

      return {
        success: false,
        error: errorMessage
      }
    } finally {
      isLoading.value = false
    }
  }

  const signInByCard = async (credentials: { smartCardNo: string; systemAlias?: string | null }) => {
    isLoading.value = true
    try {
      resetStartupActionSession()
      // Only clear auth token from localStorage, not cookies (server needs session/CSRF for login)
      localStorage.removeItem('auth_token')

      await apiService.userSignInByCard({
        smartCardNo: credentials.smartCardNo,
        systemAlias: credentials.systemAlias || null,
        clientVersionDate: getClientVersionDate(),
      })

      // Wait for cookies to be set by browser (Spring Security sets them via Set-Cookie headers)
      await new Promise(resolve => setTimeout(resolve, 500))

      // Extract token from cookie if available
      const cookieToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('X-AUTH-TOKEN='))
        ?.split('=')[1]

      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('CSRF-TOKEN='))
        ?.split('=')[1]

      // Verify we have authentication cookies
      if (!csrfToken) {
        throw new Error('Authentication failed - CSRF token not received')
      }

      const authToken = cookieToken || csrfToken || 'authenticated'
      token.value = authToken

      if (cookieToken) {
        localStorage.setItem('auth_token', cookieToken)
      }

      const storedUsername = 'user' // todo: fetch user data
      localStorage.setItem('username', storedUsername)

      // Create minimal user object. todo: fetch user data
      user.value = {
        username: storedUsername,
        email: '',
        firstName: 'User',
        lastName: '',
        roles: [],
        permissions: [],
        token: authToken
      }

      await fetchUserProfile({ force: true })

      return {
        success: true,
        user: user.value
      }

    } catch (error: any) {
      console.error('Sign in by card error:', error)

      let errorMessage = t('errors.authentication.card')

      // Clear cookies first if we get 403 during login - might be stale session
      if (error.response?.status === 403) {
        clearAllCookies()
      }

      if (error.response?.data?.message) {
        const translated = translateErrorMessage(error.response.data.message)
        errorMessage = translated || error.response.data.message
      } else if (error.response?.data?.error) {
        const translated = translateErrorMessage(error.response.data.error)
        errorMessage = translated || error.response.data.error
      } else if (error.response?.status === 400) {
        errorMessage = t('errors.cardNotFound')
      } else if (error.response?.status === 401) {
        errorMessage = t('errors.cardNotFound')
      } else if (error.response?.status === 403) {
        errorMessage = t('errors.forbidden.detailed')
      } else if (error.response?.status === 404) {
        errorMessage = t('errors.serverUnavailable')
      } else if (error.response?.status === 500) {
        errorMessage = t('errors.internalServerError')
      } else if (error.response?.status) {
        errorMessage = getErrorMessageForStatus(error.response.status)
      } else if (error.message) {
        const translated = translateErrorMessage(error.message)
        errorMessage = translated || error.message
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = t('errors.networkError')
      }

      logout()

      return {
        success: false,
        error: errorMessage
      }
    } finally {
      isLoading.value = false
    }
  }

  const signOut = async () => {
    try {
      // Используем правильный endpoint /user/signout (GET)
      await apiService.userSignOut()
    } catch (error) {
      console.warn('Logout API call failed:', error)
    } finally {
      logout()
    }
  }

  const refreshToken = async (): Promise<string> => {
    try {
      if (!token.value) {
        throw new Error('No token to refresh')
      }

      const responseData = await apiService.refreshToken({ token: token.value })

      // Анализируем ответ refresh token
      const newToken = responseData.token || responseData

      if (!newToken) {
        throw new Error('No token in refresh response')
      }

      token.value = newToken
      localStorage.setItem('auth_token', newToken)

      return newToken
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
      throw error
    }
  }

  const login = async (credentials: { username: string; password: string }) => {
    // Перенаправляем на signIn для реальной авторизации
    return await signIn(credentials)
  }

  const logout = (): void => {
    const appStore = useAppStore()
    appStore.removeAllDialogs()
    appStore.removeAllOverlays()
    appStore.clearPlatformPagesCache({ preserveMainFrame: false })
    appStore.clearUserScopedConfig()
    resetStartupActionSession()
    user.value = null
    token.value = null
    clearAllCookies()
    usePlatformConnectionStore().reset()
    useUserPresenceStore().reset()
    useMenuNotificationIgnoreStore().clear()
    useMenuFocusStore().reset()
  }

  const checkAuth = async (): Promise<boolean> => {
    // Защита от повторных вызовов
    if (isLoading.value) {
      return isAuthenticated.value
    }

    if (!token.value) {
      return false
    }

    isLoading.value = true

    try {
      if (profileLoaded.value) {
        return await verifySession()
      }
      return await fetchUserProfile({ force: true })
    } catch (error: any) {
      const status = error.response?.status

      // Для ошибок аутентификации очищаем данные
      if (status === 400 || status === 401 || status === 403) {
        logout()
        return false
      }

      return false

    } finally {
      isLoading.value = false
    }
  }

  // todo: remove or implement in settings page
  const changePassword = async (passwordData: {
    oldPassword: string;
    newPassword: string
  }) => {
    try {
      await apiService.post('/user/changePassword', passwordData)
      return { success: true }
    } catch (error: any) {
      console.error('Password change failed:', error)
      return {
        success: false,
        error: error.response?.data?.message || t('errors.passwordChange')
      }
    }
  }

  return {
    // State
    user,
    token,
    isLoading,

    // Getters
    isAuthenticated,
    userFullName,
    profileLoaded,
    platformProfile,
    roleIds,
    lineIds,

    // Actions
    initAuth,
    fetchUserProfile,
    verifySession,
    patchUserFields,
    signIn,
    signInByCard,
    signOut,
    login,
    logout,
    checkAuth,
    refreshToken,
    changePassword
  }
})
